import { CircleStop, Ellipsis, PencilLine, Trash2, Users } from 'lucide-react';

import {
  CampaignStatus,
  isCampaignDeletable,
  mergeResponseTrackingTerms,
  type CampaignListItemResponse,
} from '@/entities/campaign';
import { ProfileImage } from '@/shared/components/profile-image/profile-image';
import { Badge } from '@/shared/components/shadcn/badge';
import { Button } from '@/shared/components/shadcn/button';
import { Card } from '@/shared/components/shadcn/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/shadcn/dropdown-menu';
import { TooltipFlat } from '@/shared/components/shadcn/tooltip/tooltip-flat';
import { CampaignKeywordChip } from '../../campaign-editor/primitives';
import { statusConfigs } from '../campaign-detail/detail-constants';
import {
  formatCampaignPeriod,
  getCampaignEndingCountdown,
  getCampaignEndingTooltip,
  getKeywordPreview,
  handleCardKeyboard,
} from './campaigns-page-utils';

export function CampaignCard({
  campaign,
  onDelete,
  onEnd,
  onOpen,
  onRename,
}: {
  campaign: CampaignListItemResponse;
  onDelete: () => void;
  onEnd: () => void;
  onOpen: () => void;
  onRename: () => void;
}) {
  const keywordPreview = getKeywordPreview(mergeResponseTrackingTerms(campaign));
  const canDeleteCampaign = isCampaignDeletable(campaign.status);
  const endingCountdown = getCampaignEndingCountdown(campaign);
  const endingTooltip = getCampaignEndingTooltip(campaign);

  const statusConfig = statusConfigs.get(campaign.status);

  return (
    <Card className="group flex flex-row items-start justify-between p-0 transition-shadow hover:shadow-md">
      <div
        role="button"
        tabIndex={0}
        onClick={onOpen}
        onKeyDown={(event) => handleCardKeyboard(event, onOpen)}
        className="flex flex-1 cursor-pointer flex-col gap-3 px-6 py-6 text-left focus:outline-none"
      >
        <h3 className="flex items-center gap-3">
          <span className="min-w-0 truncate text-base font-semibold">{campaign.name}</span>
          <Badge variant={statusConfig.variants} className="shrink-0">
            {statusConfig.label}
          </Badge>
          {endingCountdown ? (
            <TooltipFlat asChild side="top" content={endingTooltip ?? ''}>
              <Badge
                variant="outline"
                tabIndex={0}
                className="bg-destructive/10 text-destructive hover:bg-destructive/10 shrink-0 border-transparent outline-none"
              >
                {endingCountdown}
              </Badge>
            </TooltipFlat>
          ) : null}
        </h3>

        <div className="text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
          {campaign.status !== CampaignStatus.SCHEDULED && (
            <>
              <div className="flex items-center gap-2.5">
                <CreatorPreviewGroup avatars={campaign.profile_image_urls} />
                <span className="text-foreground font-semibold">
                  {campaign.creator_count} {campaign.creator_count === 1 ? 'creator' : 'creators'}
                </span>
              </div>

              <div className="border-border h-4 border-l" aria-hidden="true" />
            </>
          )}

          <p>{formatCampaignPeriod(campaign.start_date, campaign.end_date)}</p>
        </div>
      </div>

      <div
        className="flex shrink-0 items-center gap-3 self-center px-6 py-6"
        onClick={(event) => event.stopPropagation()}
      >
        {keywordPreview.items.length > 0 || keywordPreview.hiddenCount > 0 ? (
          <div className="flex max-w-80 items-center gap-2">
            {keywordPreview.items.map((keyword) => (
              <CampaignKeywordChip key={keyword} label={keyword} className="max-w-30" />
            ))}
            {keywordPreview.hiddenCount > 0 ? (
              <CampaignKeywordChip label={`+${keywordPreview.hiddenCount}`} />
            ) : null}
          </div>
        ) : null}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              <Ellipsis className="size-4" />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem onClick={onRename}>
              <PencilLine className="size-4" />
              Rename
            </DropdownMenuItem>
            {campaign.status !== CampaignStatus.ENDED ? (
              <DropdownMenuItem onClick={onEnd}>
                <CircleStop className="size-4" />
                End
              </DropdownMenuItem>
            ) : null}
            {canDeleteCampaign ? (
              <DropdownMenuItem variant="destructive" onClick={onDelete}>
                <Trash2 className="size-4" />
                Delete
              </DropdownMenuItem>
            ) : (
              <TooltipFlat asChild side="left" content="Active campaigns cannot be deleted.">
                <div tabIndex={0} className="outline-hidden">
                  <DropdownMenuItem variant="destructive" disabled>
                    <Trash2 className="size-4" />
                    Delete
                  </DropdownMenuItem>
                </div>
              </TooltipFlat>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </Card>
  );
}

function CreatorPreviewGroup({ avatars }: { avatars?: string[] }) {
  if (!avatars || avatars.length === 0) {
    return (
      <div
        className="bg-muted text-muted-foreground flex size-7 items-center justify-center rounded-full"
        aria-hidden="true"
      >
        <Users className="size-4" />
      </div>
    );
  }

  return (
    <div className="flex -space-x-2">
      {avatars.slice(0, 3).map((avatar) => (
        <ProfileImage
          key={avatar}
          src={avatar}
          name={avatar}
          className="ring-background size-7 rounded-full object-cover ring-2"
        />
      ))}
    </div>
  );
}
